import type { CodeEditorState } from '@/app/atoms/codeEditorAtom';
import { codeEditorAtom } from '@/app/atoms/codeEditorAtom';
import type { ContextMenuState } from '@/app/atoms/contextMenuAtom';
import { contextMenuAtom, ContextMenuType } from '@/app/atoms/contextMenuAtom';
import type { EditorInteractionState } from '@/app/atoms/editorInteractionStateAtom';
import { editorInteractionStateAtom } from '@/app/atoms/editorInteractionStateAtom';
import type { GridPanMode } from '@/app/atoms/gridPanModeAtom';
import { gridPanModeAtom, gridPanMode as gridPanModeDerivedAtom, PanMode } from '@/app/atoms/gridPanModeAtom';
import type { GridSettings } from '@/app/atoms/gridSettingsAtom';
import { gridSettingsAtom } from '@/app/atoms/gridSettingsAtom';
import type { InlineEditorState } from '@/app/atoms/inlineEditorAtom';
import { inlineEditorAtom } from '@/app/atoms/inlineEditorAtom';
import { store } from '@/app/atoms/store';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { inlineEditorHandler } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorHandler';
import type { CursorMode } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorKeyboard';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { embedHideGridLines, embedHideHeadings } from '@/app/helpers/isEmbed';
import { multiplayer } from '@/app/web-workers/multiplayerWebWorker/multiplayer';
import type { GlobalSnackbar, SnackbarOptions } from '@/shared/components/GlobalSnackbarProvider';
import type { ApiTypes } from 'quadratic-shared/typesAndSchemas';
import type { JSX } from 'react';

interface Input {
  show: boolean;
  initialValue?: string;
  value?: string;
  cursor?: number;
  x?: number;
  y?: number;
  sheetId?: string;
}

let _input: Input = { show: false };
let waitingForSnackbar: { message: JSX.Element | string; options: SnackbarOptions }[] = [];
let _addGlobalSnackbar: GlobalSnackbar['addGlobalSnackbar'] | undefined;
let _closeCurrentSnackbar: (() => void) | undefined;

export let unsavedEditorChanges: string | undefined;
export let temporarilyHideCellTypeOutlines = false;

export const setUnsavedEditorChanges = (value: string | undefined) => {
  unsavedEditorChanges = value;
};

export const setTemporarilyHideCellTypeOutlines = (value: boolean) => {
  temporarilyHideCellTypeOutlines = value;
};

export const getGridPanMode = () => store.get(gridPanModeAtom);
export const setGridPanMode = (value: GridPanMode) => store.set(gridPanModeAtom, value);

export const getGridSettings = () => store.get(gridSettingsAtom);
export const setGridSettings = (value: GridSettings) => store.set(gridSettingsAtom, value);

export const getEditorInteractionState = () => store.get(editorInteractionStateAtom);
export const setEditorInteractionState = (value: EditorInteractionState) =>
  store.set(editorInteractionStateAtom, value);
export const updateEditorInteractionState = (fn: (prev: EditorInteractionState) => EditorInteractionState) => {
  store.set(editorInteractionStateAtom, fn(store.get(editorInteractionStateAtom)));
};

export const getInlineEditorState = () => store.get(inlineEditorAtom);
export const setInlineEditorState = (fn: (prev: InlineEditorState) => InlineEditorState) => {
  store.set(inlineEditorAtom, fn(store.get(inlineEditorAtom)));
};

export const getCodeEditorState = () => store.get(codeEditorAtom);
export const setCodeEditorState = (value: CodeEditorState) => store.set(codeEditorAtom, value);
export const updateCodeEditorState = (fn: (prev: CodeEditorState) => CodeEditorState) => {
  store.set(codeEditorAtom, fn(store.get(codeEditorAtom)));
};

export const getContextMenu = () => store.get(contextMenuAtom);
export const setContextMenu = (value: ContextMenuState) => store.set(contextMenuAtom, value);

export const getPanMode = () => store.get(gridPanModeDerivedAtom);

export const getPermissions = (): ApiTypes['/v0/files/:uuid.GET.response']['userMakingRequest']['filePermissions'] => {
  return getEditorInteractionState().permissions;
};

export const showGridLines = (): boolean => {
  const settings = getGridSettings();
  return !settings.presentationMode && settings.showGridLines && !embedHideGridLines;
};

export const showHeadings = (): boolean => {
  const settings = getGridSettings();
  return !settings.presentationMode && settings.showHeadings && !embedHideHeadings;
};

export const showCellTypeOutlines = (): boolean => {
  const settings = getGridSettings();
  return !temporarilyHideCellTypeOutlines && !settings.presentationMode && settings.showCellTypeOutlines;
};

export const presentationMode = (): boolean => {
  return getGridSettings().presentationMode;
};

export const showA1Notation = (): boolean => {
  const codeEditorState = getCodeEditorState();
  if (
    (codeEditorState.showCodeEditor && codeEditorState.codeCell.language === 'Formula') ||
    inlineEditorHandler.isEditingFormula()
  ) {
    return true;
  }
  return getGridSettings().showA1Notation;
};

export const showCodePeek = (): boolean => {
  const settings = getGridSettings();
  return !settings.presentationMode && settings.showCodePeek;
};

export const changeInput = (input: boolean, initialValue?: string, cursorMode?: CursorMode) => {
  if (input === false) {
    multiplayer.sendEndCellEdit();
  }
  if (_input.show === true && _input.x !== undefined && _input.y !== undefined && _input.sheetId !== undefined) {
    content.cellsSheets.showLabel(_input.x, _input.y, _input.sheetId, true);
  }
  if (input === true) {
    const x = sheets.sheet.cursor.position.x;
    const y = sheets.sheet.cursor.position.y;
    if (multiplayer.cellIsBeingEdited(x, y, sheets.current)) {
      _input = { show: false };
    } else {
      _input = { show: input, initialValue, x, y, sheetId: sheets.current };
      content.cellsSheets.showLabel(x, y, sheets.current, false);
    }
  } else {
    _input = { show: false };
  }
  events.emit('setDirty', { cursor: true });
  events.emit('changeInput', input, initialValue, cursorMode);
};

export const getInput = () => _input;

export const isRenamingTable = (): boolean => {
  const contextMenu = getContextMenu();
  return !!(
    (contextMenu.type === ContextMenuType.Table || contextMenu.type === ContextMenuType.TableColumn) &&
    contextMenu.rename
  );
};

export const isGridFocused = (): boolean => {
  const contextMenu = getContextMenu();
  return document.activeElement === pixiApp.canvas || _input.show || contextMenu.type !== undefined;
};

export const setGlobalSnackbar = (
  addGlobalSnackbar: GlobalSnackbar['addGlobalSnackbar'],
  closeGlobalSnackbar: () => void
) => {
  _addGlobalSnackbar = addGlobalSnackbar;
  _closeCurrentSnackbar = closeGlobalSnackbar;
  for (const s of waitingForSnackbar) {
    _addGlobalSnackbar(s.message, s.options);
  }
  waitingForSnackbar = [];
};

export const snackbar = (message: string, options: SnackbarOptions) => {
  if (_addGlobalSnackbar) {
    _addGlobalSnackbar(message, options);
  } else {
    waitingForSnackbar.push({ message, options });
  }
};

export const addGlobalSnackbar: GlobalSnackbar['addGlobalSnackbar'] | undefined = (
  message: JSX.Element | string,
  options?: SnackbarOptions
) => {
  if (_addGlobalSnackbar && options) {
    _addGlobalSnackbar(message, options);
  } else if (options) {
    waitingForSnackbar.push({ message, options });
  }
};

export const closeCurrentSnackbar = () => {
  _closeCurrentSnackbar?.();
};

export const setAddGlobalSnackbar = (value: GlobalSnackbar['addGlobalSnackbar'] | undefined) => {
  _addGlobalSnackbar = value;
};

export const setCloseCurrentSnackbar = (value: (() => void) | undefined) => {
  _closeCurrentSnackbar = value;
};

export const updateGridPanModeFromState = (gridPanMode: GridPanMode) => {
  if (gridPanMode.panMode === PanMode.Enabled) {
    pixiApp.canvas.style.cursor = 'grab';
  } else if (gridPanMode.panMode === PanMode.Dragging) {
    pixiApp.canvas.style.cursor = 'grabbing';
  } else if (gridPanMode.panMode !== PanMode.Disabled) {
    pixiApp.canvas.style.cursor = 'unset';
  }
};

export const resetGridState = () => {
  _input = { show: false };
  unsavedEditorChanges = undefined;
  temporarilyHideCellTypeOutlines = false;
};
